import React, {useEffect, useState, useRef} from 'react';
import { IInitialData, IBanner, IVerifyResponse, IErrorResponse } from './types';
import ReCAPTCHA from "react-google-recaptcha";
import Alert from './components/Alert';
// Định nghĩa type cho initial data
const initialData = window.__INITIAL_DATA__;
declare global {
  interface Window {
    __INITIAL_DATA__: IInitialData;
  }
}

interface BannerProps {
  position: 'top' | 'bottom';
  banner: IBanner;
}

const Banner: React.FC<BannerProps> = ({ position, banner }) => {
  if (!banner?.image) return null;

  return (
    <div className={`banner-${position}`}>
      <a href={banner.link} target="_blank" rel="noopener noreferrer">
        <img src={banner.image} alt="Banner" className="w-full h-auto" />
      </a>
    </div>
  );
};


function App() {
  const [productCode, setProductCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    success?: IVerifyResponse;
    error?: string;
  }>({});
  
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const {brandConfig, apiUrl, publicUrl} = initialData;
  const brandCode = initialData.brand;
  const {brand, verify_config, information_config, banners } = brandConfig;


  useEffect(() => {
    // Lấy mã sản phẩm từ URL params
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get('code');
    if (codeFromUrl) {
      setProductCode(codeFromUrl);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!productCode.trim()) {
      alert('Vui lòng nhập mã sản phẩm');
      return;
    }

    if (!isVerified) {
      alert('Vui lòng xác nhận bạn không phải robot');
      return;
    }

    try {
      setIsLoading(true);
      const token = await recaptchaRef.current?.getValue();
      
      const response = await fetch(`${apiUrl}/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand: brandCode,
          code: productCode,
          recaptchaToken: token
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult({ success: data });
      } else {
        setResult({ error: data.error });
      }

    } catch (error) {
      setResult({ error: 'Có lỗi xảy ra, vui lòng thử lại sau' });
    } finally {
      setIsLoading(false);
      recaptchaRef.current?.reset();
      setIsVerified(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setIsVerified(!!token);
  };

  useEffect(() => {
    console.log('initialData', initialData);
  }, []);

  return (
    <div className='app-wraper bg-[#f5f5f5]'>
      <div className="max-w-4xl mx-auto flex flex-col min-h-screen bg-white shadow-sm">
        {/* Header */}
        <header className="border-b bg-white p-4 flex ">
          <div className="mx-auto max-w-4xl">
            <img 
              src={brand?.logo || '/logo.png'} 
              alt="Logo"
              className="h-12 w-auto" 
            />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 ">

          {banners?.banner_top && (
            <Banner position="top" banner={banners.banner_top} />
          )}

          <div className="mx-auto max-w-2xl px-4 py-8">
            {verify_config?.process_content ? (
              <div dangerouslySetInnerHTML={{ __html: verify_config.process_content }} />
            ) : (
              <div className="p-8">
              <h1 className="title-page mb-6 text-2xl text-center font-bold">
                KIỂM TRA HÀNG THẬT
              </h1>
              
              <p className="mb-8 text-center text-gray-600">
                Hướng dẫn kiểm tra các sản phẩm chính hãng được phân phối bởi <span className="font-semibold text-teal-600">SCI-DERM</span>
              </p>

              <div className="mb-8 space-y-4">
                <div className="flex items-start gap-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-200 text-pink-800">1</span>
                  <p className="flex-1 text-gray-600">Nhập mã sản phẩm trong tem được dán trên sản phẩm. Lưu ý nhập đúng mã sản phẩm, không nhập khoảng trắng và ký tự đặc biệt</p>
                </div>

                <div className="flex items-start gap-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-200 text-pink-800">2</span>
                  <p className="flex-1 text-gray-600">Bấm xác nhận không phải là robot.</p>
                </div>

                <div className="flex items-start gap-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-200 text-pink-800">3</span>
                  <p className="flex-1 text-gray-600">Bấm vào nút KIỂM TRA và xem thông báo của hệ thống.</p>
                </div>
              </div>
            </div>
            )}
          </div>

            <div className='wraper-form max-w-xl mx-auto mt-10 mb-20 bg-[#f5f5f5] rounded-lg shadow-md p-8'>
              <h3 className="text-2xl font-bold text-center text-primary mb-8 uppercase">
                Kiểm tra sản phẩm
              </h3>

              {/* Hiển thị kết quả sử dụng Alert component */}
              {result.success && (
                <Alert
                  type="success"
                  message={result.success.message}
                  isHtml={true}
                  number_verify={result.success.number_verify}
                  max_number_verify={result.success.max_number_verify}
                  onClose={() => setResult({})}
                />
              )}

              {result.error && (
                <Alert
                  type="error"
                  message={result.error}
                  onClose={() => setResult({})}
                />
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  value={productCode}
                  onChange={(e) => setProductCode(e.target.value)}
                  placeholder="Nhập mã sản phẩm"
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={isLoading}
                />

                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LcghMIjAAAAAEo8TTgNdgJvHcSsAt7Kwn63wcIN" // Thay thế bằng site key của bạn
                    onChange={handleRecaptchaChange}
                  />
                </div>

                <button
                  type="submit"
                  disabled={!isVerified || isLoading}
                  className="w-full rounded-md bg-primary px-4 py-3 text-white hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Đang xử lý...' : 'Kiểm tra'}
                </button>
              </form>
              
              <div className="note mt-6 text-center text-gray-500">
                *Gọi ngay <a href={`tel:${information_config?.company_phone?.replace(/^0/, '+84')}`} className="text-teal-600 hover:text-teal-700 font-medium">{information_config?.company_phone?.replace(/(\d{4})(\d{3})(\d{3})/, '$1.$2.$3')}</a> nếu bạn cần hỗ trợ.
              </div>
            </div>

            {banners?.banner_bottom && (
              <Banner position="top" banner={banners.banner_bottom} />
            )}
        </main>

        {/* Footer */}
        <footer className="border-t bg-primary text-white">
          <div className="footer-infomation p-8 text-center justify-center">
              <h3 className='mb-3 font-bold text-lg'>{information_config?.company_name}</h3>
              <p className='mb-1'>Email: <a className='text-white' href={`mailto:${information_config?.company_email}`}>{information_config?.company_email}</a></p>
              <p className='mb-1'>Hotline: <a  href={`tel:${information_config?.company_phone?.replace(/^0/, '+84')}`}>{information_config?.company_phone?.replace(/(\d{4})(\d{3})(\d{3})/, '$1.$2.$3')}</a></p>
          </div>

          <div className="mx-auto max-w-4xl border-t border-gray-400 px-8 py-3 text-center text-sm">
            Copyright © {new Date().getFullYear()} by {information_config?.company_name}. All rights reserved.
          </div>
        </footer>
      </div>
    </div>
    
  );
}

export default App;
